import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export interface TablePaginationProps {
  /** Zero-based. */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizes?: number[];
  className?: string;
}

/** Under a table: rows per page, where you are, previous/next. */
export function TablePagination({ page, pageSize, total, onPageChange, onPageSizeChange, pageSizes = [10, 25, 50], className }: TablePaginationProps) {
  const { t } = useTranslation();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total ? page * pageSize + 1 : 0;
  const to = Math.min(total, (page + 1) * pageSize);

  return (
    <div className={cn('flex flex-wrap items-center justify-end gap-4 px-4 py-3 text-sm text-muted-foreground', className)} data-testid="table-pagination">
      {onPageSizeChange && (
        <div className="flex items-center gap-2">
          <span>{t('ADMIN_SHELL.COMMON.ROWS_PER_PAGE')}</span>
          <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
            <SelectTrigger size="sm" className="w-18" aria-label={t('ADMIN_SHELL.COMMON.ROWS_PER_PAGE')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizes.map((s) => (
                <SelectItem key={s} value={String(s)}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <span className="tabular-nums">{t('ADMIN_SHELL.COMMON.RANGE', { from, to, total })}</span>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon-sm" disabled={page <= 0} onClick={() => onPageChange(page - 1)} aria-label={t('ADMIN_SHELL.COMMON.PREVIOUS')}>
          <ChevronLeft className="rtl:rotate-180" />
        </Button>
        <Button variant="ghost" size="icon-sm" disabled={page >= pages - 1} onClick={() => onPageChange(page + 1)} aria-label={t('ADMIN_SHELL.COMMON.NEXT')}>
          <ChevronRight className="rtl:rotate-180" />
        </Button>
      </div>
    </div>
  );
}
